import { z } from 'zod';
import { Cursor, Id, Limit, Timestamp } from './common';
import { BINARY, NoContent, endpoint } from './endpoint';
import { lenient } from './lenient';
import { LIMITS, cleanFileName } from './limits';

/**
 * Malware scan state. Files stay `pending` while the scanner is down —
 * they are never treated as clean by default. `unscannable` (e.g. encrypted
 * archives) may be sent and downloaded only with an explicit acknowledgement.
 */
export const ScanStatus = z.enum(['pending', 'scanning', 'clean', 'infected', 'unscannable', 'error']);
export type ScanStatus = z.infer<typeof ScanStatus>;

export const ScanDetail = z.enum(['encrypted_archive', 'scanner_unavailable', 'archive_limits', 'type_mismatch']);
export type ScanDetail = z.infer<typeof ScanDetail>;

export const FileInfo = z.object({
  id: Id,
  conversationId: Id,
  messageId: Id.nullable(),
  uploaderId: Id,
  name: z.string(),
  /** Lower-case extension without the dot ("pdf"). */
  ext: z.string(),
  kind: lenient(z.enum(['document', 'image', 'archive', 'other']), 'other'),
  /** MIME type detected from the file signature, not the client's claim. */
  mimeType: z.string(),
  size: z.number().int().nonnegative(),
  /** An unknown state from a newer server reads as `pending`: never downloadable by mistake. */
  scan: lenient(ScanStatus, 'pending'),
  scanDetail: lenient(ScanDetail.nullable(), null),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  createdAt: Timestamp,
  /** A program or installer, sent where the company allows them: receivers get a warning first. Absent when not. */
  executable: z.boolean().optional(),
});
export type FileInfo = z.infer<typeof FileInfo>;

export const filesEndpoints = {
  /** Reserves disk space atomically before any bytes are sent. */
  createUpload: endpoint({
    id: 'files.createUpload',
    method: 'POST',
    path: '/api/v1/uploads',
    auth: 'session',
    body: z.object({
      conversationId: Id,
      /** Stored and shown cleaned (`cleanFileName`): a bidi override cannot fake the extension people read. */
      fileName: z.string().transform(cleanFileName).pipe(z.string().trim().min(1).max(255)),
      size: z.number().int().min(0).max(LIMITS.maxFileBytes),
      mimeType: z.string().max(255).optional(),
    }),
    response: z.object({ uploadId: Id, expiresAt: Timestamp }),
    successStatus: 201,
    rateLimit: 'upload',
  }),
  /** Raw bytes; Content-Length must equal the declared size. Returns the file in `pending`/`scanning`. */
  putUploadContent: endpoint({
    id: 'files.putUploadContent',
    method: 'PUT',
    path: '/api/v1/uploads/:uploadId/content',
    auth: 'session',
    params: z.object({ uploadId: Id }),
    body: z.custom<Blob | ArrayBuffer | Uint8Array | ReadableStream<Uint8Array>>(),
    rawBody: true,
    response: z.object({ file: FileInfo }),
    rateLimit: 'upload',
  }),
  /** Cancels an upload and releases its reservation. */
  cancelUpload: endpoint({
    id: 'files.cancelUpload',
    method: 'DELETE',
    path: '/api/v1/uploads/:uploadId',
    auth: 'session',
    params: z.object({ uploadId: Id }),
    response: NoContent,
  }),
  get: endpoint({
    id: 'files.get',
    method: 'GET',
    path: '/api/v1/files/:fileId',
    auth: 'session',
    params: z.object({ fileId: Id }),
    response: z.object({ file: FileInfo }),
  }),
  /**
   * Streams the original after re-checking permission on every request.
   * Supports Range; RFC 6266/5987 filename for Korean names.
   * `pending/scanning` → FILE_NOT_READY, `infected` → FILE_INFECTED,
   * `unscannable` without `acknowledgeUnscanned=true` → FILE_UNSCANNED_ACK_REQUIRED.
   */
  download: endpoint({
    id: 'files.download',
    method: 'GET',
    path: '/api/v1/files/:fileId/download',
    auth: 'session',
    params: z.object({ fileId: Id }),
    query: z.object({ acknowledgeUnscanned: z.stringbool().optional() }),
    response: BINARY,
  }),
  /** Inline image preview (clean png/jpg/gif/webp) and in-place playback of clean video and audio (Range). */
  preview: endpoint({
    id: 'files.preview',
    method: 'GET',
    path: '/api/v1/files/:fileId/preview',
    auth: 'session',
    params: z.object({ fileId: Id }),
    response: BINARY,
  }),
  /**
   * A clean JPEG, PNG or still WebP image made small (capability `files.thumbnails`): WebP with its long edge at most
   * 1024 px for a tile in a conversation (`tile`, the default) or 2048 px for the photo viewer (`screen`).
   * NOT_FOUND where none can be made (an animated image, a GIF): the app uses `preview` then.
   */
  thumbnail: endpoint({
    id: 'files.thumbnail',
    method: 'GET',
    path: '/api/v1/files/:fileId/thumbnail',
    auth: 'session',
    params: z.object({ fileId: Id }),
    query: z.object({ size: z.enum(['tile', 'screen']).optional() }),
    response: BINARY,
  }),
  listForConversation: endpoint({
    id: 'files.listForConversation',
    method: 'GET',
    path: '/api/v1/conversations/:conversationId/files',
    auth: 'session',
    params: z.object({ conversationId: Id }),
    query: z.object({ kind: z.enum(['image', 'other']).optional(), cursor: Cursor.optional(), limit: Limit(100, 30) }),
    response: z.object({ files: z.array(FileInfo), nextCursor: Cursor.nullable() }),
  }),
};
